// ===========================================
// Mark Refunded Dialog (#642 D13)
// ===========================================
// Closes ONE open Refund due for its own amount (shown, never typed): the bank day the money
// went back (not after the school's today) and the bank reference. Used by the family's credit
// panel and the dashboard's Refunds due card.

import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useSchoolTimezone } from '../../../contexts/TenantContext';
import { useMarkRefunded } from '../../../hooks/useFamilyCredit';
import { schoolTodayUtcMidnight, toUTCDateKey } from '../../../utils/dateUtils';
import { apiErrorMessage } from '../../../utils/apiError';
import { formatCurrencyFromDollars } from '../../../utils/currency';

interface MarkRefundedDialogProps {
  open: boolean;
  onClose: () => void;
  /** The open Refund due being closed. */
  due: { id: string; amount: string; familyName?: string } | null;
}

export default function MarkRefundedDialog({ open, onClose, due }: MarkRefundedDialogProps) {
  const timeZone = useSchoolTimezone();
  const todayKey = toUTCDateKey(schoolTodayUtcMidnight(timeZone));
  const [refundedOn, setRefundedOn] = useState(todayKey);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const mutation = useMarkRefunded();

  const close = () => {
    setRefundedOn(todayKey);
    setReference('');
    setNote('');
    mutation.reset();
    onClose();
  };

  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(refundedOn) && refundedOn <= todayKey;
  const valid = !!due && dateValid && reference.trim().length > 0;

  const confirm = async () => {
    if (!due || !valid) return;
    try {
      await mutation.mutateAsync({
        entryId: due.id,
        refundedOn,
        reference: reference.trim(),
        note: note.trim() || undefined,
      });
      close();
    } catch {
      // Shown below, as the server worded it.
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>Mark as refunded</DialogTitle>
      <DialogContent>
        {due && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {formatCurrencyFromDollars(parseFloat(due.amount))} refunded
            {due.familyName ? ` to ${due.familyName}` : ''}.
          </Typography>
        )}
        <TextField
          fullWidth
          required
          type="date"
          label="Refunded on"
          value={refundedOn}
          onChange={(e) => setRefundedOn(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: todayKey }}
          error={refundedOn !== '' && !dateValid}
          helperText={refundedOn !== '' && !dateValid ? 'The refund date cannot be in the future' : 'The day the money went back'}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          required
          label="Bank reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          inputProps={{ maxLength: 100 }}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 500 }}
        />
        {mutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {apiErrorMessage(mutation.error, 'Could not record the refund')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={confirm}
          disabled={!valid || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Mark refunded
        </Button>
      </DialogActions>
    </Dialog>
  );
}
